import { gql } from '@apollo/client';

// GraphQL
import { client } from '../../../../../../gql/client';
import {
  GET_INTERLOCUTOR_GROUP_BY_ID,
  GET_MEDIA_OF_COMMUNICATION_BY_ID,
} from '../../../../../../gql/queries/communication';
import {
  GET_CAPITOL_BY_ID,
  GET_CLASSIFICATION_BY_ID_OR_ROID,
  GET_SUB_CAPITOL_BY_ID,
} from '../../../../../../gql/queries/orders/predefinedData';
import {
  GET_CONCESSION_BY_ID,
  GET_ROAD_BY_ID,
} from '../../../../../../gql/queries/geoPositions/roads';

// Types
import { PassDataParams } from './types';
import { laneLabel, trackLabel } from '../../../../../../utils/catalogLabels';
import { GET_TEAM_BY_ID } from '../../../../../../gql/queries/users';

export const passData = async ({
  formData,
  tabSelected,
  storedDataOTSelected,
}: PassDataParams) => {
  let classificationPass, capitolPass, subCapitolPass, predefinePass;
  const operator = formData.get('operator');
  const registerInitDate = formData.get('registerInitDate');
  const groupInterId = formData.get('groupInter');
  const mediaId = formData.get('media');
  const sendStatus = formData.get('sendStatus');
  const concessionId = formData.get('concession');
  const roadId = formData.get('road');
  const classificationId = formData.get('classification');
  const capitolId = formData.get('capitol');
  const subCapitolId = formData.get('subCapitol');

  // ✅ M2M: Handle teamRelationships / team / teams (backward compatible).
  // Tarea #8: el tab "Crear nueva OT" ahora escribe teamRelationships al form.
  const teamIdRaw =
    formData.get('teamRelationships') ||
    formData.get('team') ||
    formData.get('teams');
  const teamIds: string[] = !teamIdRaw
    ? []
    : typeof teamIdRaw === 'string'
      ? teamIdRaw.split(',').filter(Boolean)
      : Array.isArray(teamIdRaw)
        ? (teamIdRaw.filter((item): item is string => typeof item === 'string'))
        : (typeof teamIdRaw === 'string' ? [teamIdRaw] : []);

  const [
    {
      data: { concession },
    },
    {
      data: { road },
    },
    {
      data: { interlocutorGroup },
    },
    {
      data: { mediaOfCommunication },
    },
  ] = await Promise.all([
    client.query({
      query: GET_CONCESSION_BY_ID,
      variables: {
        id: concessionId,
      },
    }),
    client.query({
      query: GET_ROAD_BY_ID,
      variables: {
        id: roadId,
      },
    }),
    client.query({
      query: GET_INTERLOCUTOR_GROUP_BY_ID,
      variables: {
        id: groupInterId,
      },
    }),
    client.query({
      query: GET_MEDIA_OF_COMMUNICATION_BY_ID,
      variables: {
        id: mediaId,
      },
    }),
  ]);

  // ✅ M2M: Fetch ALL teams, not just one
  let teamValues: any[] = [];
  if (teamIds.length > 0) {
    const teamQueries = teamIds.map((teamId: string) =>
      client.query({
        query: GET_TEAM_BY_ID,
        variables: { id: teamId },
      })
    );
    const teamResults = await Promise.all(teamQueries);
    teamValues = teamResults.map((result) => result.data.team);
  }

  // Always fetch display names from the form's own IDs (all tabs now have their own values)
  if (classificationId || capitolId || subCapitolId) {
    const queries = [];
    if (classificationId) {
      queries.push(
        client.query({
          query: GET_CLASSIFICATION_BY_ID_OR_ROID,
          variables: { id: classificationId },
        })
      );
    }
    if (capitolId) {
      queries.push(
        client.query({
          query: GET_CAPITOL_BY_ID,
          variables: { id: capitolId },
        })
      );
    }
    if (subCapitolId) {
      queries.push(
        client.query({
          query: GET_SUB_CAPITOL_BY_ID,
          variables: { id: subCapitolId },
        })
      );
    }

    const results = await Promise.all(queries);
    let resultIndex = 0;
    if (classificationId) {
      classificationPass = results[resultIndex]?.data?.classification?.name;
      resultIndex++;
    }
    if (capitolId) {
      capitolPass = results[resultIndex]?.data?.capitol?.name;
      resultIndex++;
    }
    if (subCapitolId) {
      subCapitolPass = results[resultIndex]?.data?.subCapitol?.name;
    }
  }

  // T36: Resolve predefinedOrder name from Apollo cache
  const predefinedOrderId = formData.get('predefine');
  if (predefinedOrderId) {
    const predefinedOrderData = client.readFragment({
      id: `PredefinedOrder:${predefinedOrderId}`,
      fragment: gql`
        fragment PredefinedOrderName on PredefinedOrder {
          id
          name
        }
      `,
    });
    predefinePass = predefinedOrderData?.name;
  }

  const city = formData.get('city');
  const pkInitRoad = formData.get('pkInitRoad');
  const pkEndRoad = formData.get('pkEndRoad');
  const trackValue = formData.get('track');
  const direction = formData.get('direction');
  const margin = formData.get('margin');
  const laneValue = formData.get('lane');
  const pkInit = formData.get('pkInit');
  const pkEnd = formData.get('pkEnd');
  const description = formData.get('description');
  const notes = formData.get('notes');
  const specialTag = formData.get('specialTag');

  return {
    operator,
    registerInitDate,
    groupInter: interlocutorGroup?.name,
    media: mediaOfCommunication?.name,
    sendStatus,
    concession: concession?.name,
    city,
    pkInitRoad,
    pkEndRoad,
    road: road?.name,
    track: trackLabel(trackValue as string | null),
    direction,
    margin,
    lane: laneLabel(laneValue as string | null),
    pkInit,
    pkEnd,
    roadDirections: road.directions,
    description,
    notes,
    specialTag,
    classification: classificationPass,
    capitol: capitolPass,
    subCapitol: subCapitolPass,
    predefine: predefinePass,
    teams: teamValues.map((t) => t.name), // ✅ M2M: Return array of team names
    // La card del resumen usa `team` singular: se enseñan todos, unidos.
    team: teamValues.map((t) => t.name).join(', ') || undefined,
    storedDataOTSelected,
  };
};
